/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ActionsService,
  type ContentScreenDetails,
  type TActionsContentScreenDetailsContentIdParams,
  type TActionsContentScreenDetailsOfferIdParams,
  type TActionsContentScreenDetailsRecordingIdParams,
  type TActionsContentScreenDetailsVodOfferParams,
  type TActionsParams,
  type TActionsParamsUCScreenDetails,
  type TActionsRecordingParams,
  type TActionsVodOfferParams,
  type TCustomActionList,
  type TUnifiedCollectionScreenDetails
} from "@tivocorp/data-domain-lib";

import type { IActionsInteractor } from "../../interfaces/IActionsInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link ActionsService}.
 *
 * Composed by feature interactors that need content-action metadata
 * (Playback, ContentDetails, Recordings, …). The
 * {@link ApplicationFeatureArea} is fixed at construction time.
 *
 * Stateless: a fresh `ActionsService` is built per call via
 * {@link ServiceLocator}.
 */
export class ActionsInteractor
  extends BaseInteractor
  implements IActionsInteractor
{
  private static readonly TAG: string = "ActionsInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.ALL
  ) {
    super();
  }

  public async fetchOfferActions(
    params: TActionsParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TCustomActionList> {
    return this.executeGuarded(
      "fetchOfferActions",
      async (guardedSignal: AbortSignal): Promise<TCustomActionList> => {
        Log.d(ActionsInteractor.TAG, "fetchOfferActions() called");
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchOfferActions(params, isForced, guardedSignal);
      },
      options
    );
  }

  public async fetchRecordingActions(
    params: TActionsRecordingParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TCustomActionList> {
    return this.executeGuarded(
      "fetchRecordingActions",
      async (guardedSignal: AbortSignal): Promise<TCustomActionList> => {
        Log.d(ActionsInteractor.TAG, "fetchRecordingActions() called");
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchRecordingActions(params, isForced, guardedSignal);
      },
      options
    );
  }

  public async fetchVodOfferActions(
    params: TActionsVodOfferParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TCustomActionList> {
    return this.executeGuarded(
      "fetchVodOfferActions",
      async (guardedSignal: AbortSignal): Promise<TCustomActionList> => {
        Log.d(ActionsInteractor.TAG, "fetchVodOfferActions() called");
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchVodOfferActions(params, isForced, guardedSignal);
      },
      options
    );
  }

  public async fetchContentScreenDetailsContentActions(
    params: TActionsContentScreenDetailsContentIdParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetailsContentActions",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchContentScreenDetailsContentActions() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchContentScreenDetailsContentActions(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchContentScreenDetailsVodOfferActions(
    params: TActionsContentScreenDetailsVodOfferParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetailsVodOfferActions",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchContentScreenDetailsVodOfferActions() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchContentScreenDetailsVodOfferActions(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchUnifiedCollectionScreenDetailsActions(
    params: TActionsParamsUCScreenDetails,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TUnifiedCollectionScreenDetails> {
    return this.executeGuarded(
      "fetchUnifiedCollectionScreenDetailsActions",
      async (
        guardedSignal: AbortSignal
      ): Promise<TUnifiedCollectionScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchUnifiedCollectionScreenDetailsActions() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchUnifiedCollectionScreenDetailsActions(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchContentScreenDetailsContentActionsV3(
    params: TActionsContentScreenDetailsContentIdParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetailsContentActionsV3",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchContentScreenDetailsContentActionsV3() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchContentScreenDetailsContentActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchContentScreenDetailsOfferActionsV3(
    params: TActionsContentScreenDetailsOfferIdParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetailsOfferActionsV3",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchContentScreenDetailsOfferActionsV3() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchContentScreenDetailsOfferActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchContentScreenDetailsRecordingActionsV3(
    params: TActionsContentScreenDetailsRecordingIdParams,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<ContentScreenDetails> {
    return this.executeGuarded(
      "fetchContentScreenDetailsRecordingActionsV3",
      async (guardedSignal: AbortSignal): Promise<ContentScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchContentScreenDetailsRecordingActionsV3() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchContentScreenDetailsRecordingActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }

  public async fetchUnifiedCollectionScreenDetailsActionsV3(
    params: TActionsParamsUCScreenDetails,
    isForced: boolean,
    options?: TNetworkOptions
  ): Promise<TUnifiedCollectionScreenDetails> {
    return this.executeGuarded(
      "fetchUnifiedCollectionScreenDetailsActionsV3",
      async (
        guardedSignal: AbortSignal
      ): Promise<TUnifiedCollectionScreenDetails> => {
        Log.d(
          ActionsInteractor.TAG,
          "fetchUnifiedCollectionScreenDetailsActionsV3() called"
        );
        const service: ActionsService = ServiceLocator.getInstance(
          ActionsService,
          await this.buildServicesParams(
            TIVO_SERVICES.ActionService,
            this.mFeatureArea
          )
        );
        return service.fetchUnifiedCollectionScreenDetailsActionsV3(
          params,
          isForced,
          guardedSignal
        );
      },
      options
    );
  }
}
